/**
 * Home Realm Discovery por e-mail (§6.8 do plano de billing).
 *
 * A plataforma usa 1 realm Keycloak por tenant (`tenant-<slug>`), então um
 * app não sabe, antes do login, contra qual realm inicializar o Keycloak.
 * Em vez de subdomínio + wildcard DNS/TLS, resolvemos por e-mail: o usuário
 * digita o e-mail, o backend descobre o tenant (`auth.app_user` é global,
 * e-mail único entre tenants) e devolve o realm certo.
 *
 * Por segurança, o resultado fica somente no sessionStorage. Assim, uma nova
 * aba/sessão do navegador precisa identificar novamente o usuário e o tenant.
 * A chave antiga do localStorage é removida automaticamente para não reutilizar
 * realms gravados por versões anteriores do frontend.
 */

const STORAGE_KEY = "inbim.tenant-config.v1";

export interface TenantConfig {
  /** Slug do tenant (chave de negócio, usado no header X-Tenant-Id). */
  slug: string;
  /** Nome do realm Keycloak (ex.: tenant-construtora-xyz). */
  realm: string;
  /** URL base do Keycloak. */
  keycloakUrl: string;
}

interface DiscoverRealmApiResponse {
  found: boolean;
  realm: string | null;
  slug: string | null;
  keycloakUrl: string | null;
}

export interface DiscoverTenantOptions {
  /** Base da API (mesmo valor de VITE_API_BASE_URL usado pelo http client do app). */
  apiBaseUrl?: string;
}

function removeLegacyPersistentConfig(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage pode estar indisponível; segue com armazenamento de sessão.
  }
}

/** Lê a config de tenant mantida apenas na sessão atual da aba. */
export function getStoredTenantConfig(): TenantConfig | null {
  removeLegacyPersistentConfig();
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TenantConfig>;
    if (!parsed.slug || !parsed.realm || !parsed.keycloakUrl) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed as TenantConfig;
  } catch {
    return null;
  }
}

/** Mantém a config resolvida somente durante a sessão atual da aba. */
export function storeTenantConfig(config: TenantConfig): void {
  removeLegacyPersistentConfig();
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // sessionStorage indisponível (modo privado, quota etc.) — o próximo
    // carregamento volta a pedir o e-mail.
  }
}

/** Limpa a config de sessão e qualquer resíduo persistido por versões antigas. */
export function clearStoredTenantConfig(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  removeLegacyPersistentConfig();
}

/**
 * Consulta `POST /api/auth/discover` para descobrir o tenant de um e-mail.
 * Chamada pública (sem tenant/JWT) — roda antes de qualquer inicialização
 * do Keycloak. Não lança em caso de falha de rede/HTTP — devolve `found:
 * false`, deixando a UI decidir a mensagem (não distinguir "e-mail não
 * encontrado" de "erro de rede" evita vazar informação supérflua).
 */
export async function discoverTenantByEmail(
  email: string,
  options: DiscoverTenantOptions = {},
): Promise<TenantConfig | null> {
  const base = (options.apiBaseUrl ?? "").replace(/\/$/, "");
  try {
    const response = await fetch(`${base}/api/auth/discover`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) return null;

    const body = (await response.json()) as DiscoverRealmApiResponse;
    if (!body.found || !body.realm || !body.slug || !body.keycloakUrl) {
      return null;
    }
    const config: TenantConfig = {
      slug: body.slug,
      realm: body.realm,
      keycloakUrl: body.keycloakUrl,
    };
    storeTenantConfig(config);
    return config;
  } catch {
    return null;
  }
}
